import { useAuth0 } from '@auth0/auth0-react';
import { CircularProgress } from '@mui/material';
import { ActionsProvider } from 'ActionsContext';
import { setAuthContextOnBackendService } from 'BackendAuthHandler';
import BackendService from 'BackendService';
import {
  CommonLoggedInData,
  CommonLoggedInDataContext,
  CommonLoggedInDataImpl,
  CommonLoggedInDataWithToken,
} from 'CommonLoggedInData';
import MainLayout from 'MainLayout';
import { PermissionProvider } from 'PermissionContext';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeatureProvider } from '../../../context/FeatureContext';
import { GlobalConfigProvider } from '../../../context/GlobalConfigContext';
import { FidesChatProvider } from '../../../context/FidesChatContext';
import { SetupPermissionInApp } from './adminAppPermissionBridge';

export function CheckLoginComponent({
  children,
  notLoggedInChildren,
  initialiseAssetRead,
  onAuthChange,
}: {
  children: React.ReactNode;
  notLoggedInChildren?: React.ReactNode;
  justChildren?: boolean;
  initialiseAssetRead?: boolean;
  onAuthChange?: () => void;
}) {
  const useAuthResult = useAuth0();

  const { isAuthenticated, isLoading, user, loginWithRedirect } = useAuthResult;
  const [baseData, setBaseData] = useState<CommonLoggedInData | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(true);
  const [hasTriedLogin, setHasTriedLogin] = useState(false);
  const [hasSetAuthContext, setHasSetAuthContext] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && user && !hasSetAuthContext) {
      setAuthContextOnBackendService(useAuthResult);
      setHasSetAuthContext(true);
    }
  }, [isAuthenticated, user, hasSetAuthContext, useAuthResult]);

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated && user) {
        const impl = new CommonLoggedInDataImpl(user.sub || 'irrelevant', null);
        impl.sidebarExpanded = sidebarExpanded;
        setBaseData(impl);

        if (initialiseAssetRead) {
          localStorage.removeItem('assetToken');
          new BackendService().setupAssetStorageWithRetry(true);
        }

        onAuthChange?.();
      } else {
        setBaseData(null);
      }
    }
  }, [isAuthenticated, isLoading, user, sidebarExpanded, initialiseAssetRead, onAuthChange]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasTriedLogin) {
      setHasTriedLogin(true);

      let forwardUrl = location.pathname;
      if (location.search) {
        forwardUrl += location.search;
      }
      if (location.hash) {
        forwardUrl += location.hash;
      }

      // E2E-only: lets a spec log in as a specific fixture admin identity
      // (e2e-admin@fidescapital.com.au / e2e-admin-two@fidescapital.com.au —
      // see mock-oidc-server.js's IDENTITIES_BY_SUB) by putting
      // `?login_hint=<sub>` on the URL it navigates to before this redirect
      // fires. Mirrors CheckLoginComponent.tsx's (non-admin) identical
      // passthrough — gated behind the E2E build's product-name flag (never
      // set outside run-e2e-regression.js) to keep it fully inert
      // elsewhere.
      const loginHint =
        process.env.REACT_APP_PRODUCT_NAME === 'Fides E2E'
          ? new URLSearchParams(location.search).get('login_hint')
          : null;

      loginWithRedirect({
        authorizationParams: {
          audience: process.env.REACT_APP_BACKEND_AUDIENCE,
          scope: 'openid profile email',
          redirect_uri: `${window.location.origin}/forward?forwardUrl=${encodeURIComponent(forwardUrl)}`,
          ...(loginHint ? { login_hint: loginHint } : {}),
        },
      });
    }
  }, [
    isLoading,
    isAuthenticated,
    hasTriedLogin,
    location.pathname,
    location.search,
    location.hash,
    loginWithRedirect,
  ]);

  const contextValue = useMemo(() => {
    const wrapper = new CommonLoggedInDataWithToken(baseData);
    wrapper.sidebarExpanded = sidebarExpanded;
    wrapper.setSidebarExpandedCallback = (value: boolean) => {
      setSidebarExpanded(value);
    };
    return wrapper;
  }, [baseData, sidebarExpanded]);

  if (isLoading && !baseData) {
    return (
      notLoggedInChildren || (
        <div>
          <CircularProgress />
        </div>
      )
    );
  }

  if (!hasSetAuthContext) {
    return notLoggedInChildren || <CircularProgress />;
  }

  if (contextValue?.isLoggedIn) {
    return (
      <CommonLoggedInDataContext.Provider value={contextValue}>
        {children}
      </CommonLoggedInDataContext.Provider>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isAuthenticated && !isLoading) {
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
      },
    });
    return null;
  }

  return (
    notLoggedInChildren || (
      <div>
        <CircularProgress />
      </div>
    )
  );
}

export const CheckLoginComponentWithKey = (
  props: React.JSX.IntrinsicAttributes & {
    children: React.ReactNode;
    notLoggedInChildren?: React.ReactNode;
    justChildren?: boolean | undefined;
  },
) => {
  const location = useLocation();
  return <CheckLoginComponent {...props} key={location.pathname} initialiseAssetRead={true} />;
};

export const CheckLoginComponentWithKeyAndLayout = (
  props: React.JSX.IntrinsicAttributes & {
    children: React.ReactNode;
    notLoggedInChildren?: React.ReactNode;
    justChildren?: boolean | undefined;
  },
): ReactElement => {
  const { notLoggedInChildren, justChildren } = props;
  return (
    <CheckLoginComponentWithKey
      notLoggedInChildren={notLoggedInChildren}
      justChildren={justChildren}
    >
      <PermissionProvider>
        <FeatureProvider>
          <ActionsProvider>
            <GlobalConfigProvider>
              <FidesChatProvider>
                <MainLayout>
                  <SetupPermissionInApp>{props.children}</SetupPermissionInApp>
                </MainLayout>
              </FidesChatProvider>
            </GlobalConfigProvider>
          </ActionsProvider>
        </FeatureProvider>
      </PermissionProvider>
    </CheckLoginComponentWithKey>
  );
};
